import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Save, Loader2, Plus, Trash2, Edit2, CreditCard, Zap, Users,
  Settings as SettingsIcon, Receipt, ShieldCheck, ShieldAlert, X,
} from "lucide-react";
import StripeConfigPanel from "./StripeConfigPanel";

/**
 * Admin → Billing tab.
 *   sub-tabs:  Token value  ·  Activities  ·  Packages  ·  Stripe  ·  User wallets  ·  Transactions
 */
export default function BillingTab() {
  const [stripeCfg, setStripeCfg] = useState(null);

  useEffect(() => {
    api.get("/admin/billing/stripe-config").then(({ data }) => setStripeCfg(data)).catch(() => {});
  }, []);

  const mode = stripeCfg?.mode || "test";
  const ready = stripeCfg && (mode === "test" ? stripeCfg.secret_test_configured : stripeCfg.secret_live_configured) && stripeCfg.last_test_ok;
  const isLive = mode === "live";

  return (
    <div className="space-y-6" data-testid="admin-billing-tab">
      <div className="border border-border bg-card p-6">
        <h3 className="font-display font-bold tracking-tight text-lg">Billing & Tokens</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure the token economy: token price, per-activity markup, sellable packages, and view user wallets. Stripe keys are pasted in the <b>Stripe</b> sub-tab.
        </p>
        {stripeCfg && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="stripe-mode-badge">
            {isLive ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-sm border border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300">
                <ShieldAlert className="h-3 w-3" /> STRIPE · LIVE MODE
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                <ShieldCheck className="h-3 w-3" /> STRIPE · TEST MODE
              </span>
            )}
            {ready ? (
              <span className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400">keys verified</span>
            ) : (
              <span className="text-[11px] font-mono text-amber-600 dark:text-amber-400">keys not yet verified — go to Stripe sub-tab</span>
            )}
          </div>
        )}
      </div>

      <Tabs defaultValue="settings" className="space-y-6">
        <TabsList className="rounded-sm">
          <TabsTrigger value="settings" data-testid="billing-tab-settings" className="rounded-sm"><SettingsIcon className="h-3.5 w-3.5 mr-1.5" />Token value</TabsTrigger>
          <TabsTrigger value="activities" data-testid="billing-tab-activities" className="rounded-sm"><Zap className="h-3.5 w-3.5 mr-1.5" />Activities</TabsTrigger>
          <TabsTrigger value="packages" data-testid="billing-tab-packages" className="rounded-sm"><CreditCard className="h-3.5 w-3.5 mr-1.5" />Packages</TabsTrigger>
          <TabsTrigger value="stripe" data-testid="billing-tab-stripe" className="rounded-sm"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" />Stripe</TabsTrigger>
          <TabsTrigger value="users" data-testid="billing-tab-users" className="rounded-sm"><Users className="h-3.5 w-3.5 mr-1.5" />User wallets</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="billing-tab-transactions" className="rounded-sm"><Receipt className="h-3.5 w-3.5 mr-1.5" />Transactions</TabsTrigger>
        </TabsList>

        <TabsContent value="settings"><SettingsPanel /></TabsContent>
        <TabsContent value="activities"><ActivitiesPanel /></TabsContent>
        <TabsContent value="packages"><PackagesPanel /></TabsContent>
        <TabsContent value="stripe"><StripeConfigPanel /></TabsContent>
        <TabsContent value="users"><UsersPanel /></TabsContent>
        <TabsContent value="transactions"><TransactionsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Token value / global settings
// ---------------------------------------------------------------------------
function SettingsPanel() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/billing/settings");
      setCfg(data);
      setDraft({});
    } catch (e) {
      toast.error("Failed to load billing settings");
    }
  };
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const v = (k) => (draft[k] !== undefined ? draft[k] : cfg[k]);
  const set = (k, val) => setDraft((d) => ({ ...d, [k]: val }));
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/billing/settings", draft);
      setCfg(data);
      setDraft({});
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const tokensPerDollar = 1 / (v("token_unit_price_usd") || 0.015);
  return (
    <div className="border border-border bg-card rounded-sm p-6 space-y-6">
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
        <Field label="Token unit price (USD)" hint={`1 token = $${Number(v("token_unit_price_usd") || 0).toFixed(4)}`}>
          <Input type="number" step="0.0001" min="0.0001" value={v("token_unit_price_usd") ?? 0.015} onChange={(e) => set("token_unit_price_usd", parseFloat(e.target.value))} className="rounded-sm font-mono" data-testid="setting-token-price" />
        </Field>
        <Field label="Global markup %" hint={`Applied to provider cost in passthrough mode`}>
          <Input type="number" step="1" min="0" value={v("markup_pct") ?? 30} onChange={(e) => set("markup_pct", parseFloat(e.target.value))} className="rounded-sm font-mono" data-testid="setting-markup" />
        </Field>
        <Field label="Signup bonus (tokens)" hint="One-time, on first verified signup">
          <Input type="number" min="0" value={v("signup_bonus_tokens") ?? 100} onChange={(e) => set("signup_bonus_tokens", parseInt(e.target.value, 10))} className="rounded-sm font-mono" data-testid="setting-signup-bonus" />
        </Field>
        <Field label="Low-balance threshold (tokens)" hint="Below this, users see a warning banner">
          <Input type="number" min="0" value={v("low_balance_threshold") ?? 100} onChange={(e) => set("low_balance_threshold", parseInt(e.target.value, 10))} className="rounded-sm font-mono" data-testid="setting-threshold" />
        </Field>
      </div>

      <div className="border-t border-border pt-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={!!v("free_users_block_at_zero")} onChange={(e) => set("free_users_block_at_zero", e.target.checked)} className="h-4 w-4" data-testid="setting-block-zero" />
          <span className="font-semibold">Block paid actions when balance reaches zero</span>
        </label>
        <p className="text-xs text-muted-foreground mt-1 ml-6">Users with 0 tokens cannot send chat / upload files / use voice until they top up.</p>
      </div>

      <div className="border-t border-border pt-4">
        <div className="text-xs font-mono text-muted-foreground">Quick reference:</div>
        <div className="mt-2 text-sm font-mono">
          1 USD ≈ <span className="font-bold tabular-nums">{Math.round(tokensPerDollar).toLocaleString()}</span> tokens · 1000 tokens ≈ <span className="font-bold tabular-nums">${(1000 * (v("token_unit_price_usd") || 0.015)).toFixed(2)}</span>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground mb-3">Auto-inspect (attachments)</div>
        <p className="text-xs text-muted-foreground mb-3">
          When a user attaches a file, the app can immediately summarise it via an "Auto-inspección" message. This costs one LLM call per (user, file) — permanently deduped across all chats.
        </p>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none mb-3">
          <input
            type="checkbox"
            checked={!!v("attachment_auto_inspect_enabled")}
            onChange={(e) => set("attachment_auto_inspect_enabled", e.target.checked)}
            className="h-4 w-4"
            data-testid="setting-inspect-enabled"
          />
          <span className="font-semibold">Auto-inspect uploaded files</span>
        </label>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 ml-6">
          <Field label="Min wallet balance (tokens)" hint="Below this balance, auto-inspect is skipped">
            <Input
              type="number"
              min="0"
              value={v("inspect_min_tokens") ?? 100}
              onChange={(e) => set("inspect_min_tokens", parseInt(e.target.value, 10) || 0)}
              className="rounded-sm font-mono"
              data-testid="setting-inspect-min-tokens"
            />
          </Field>
          <Field label="Max chunks in inspect prompt" hint="Cap prompt size for huge documents (≈1.2 KB / chunk)">
            <Input
              type="number"
              min="1"
              max="50"
              value={v("inspect_max_chunks") ?? 6}
              onChange={(e) => set("inspect_max_chunks", parseInt(e.target.value, 10) || 1)}
              className="rounded-sm font-mono"
              data-testid="setting-inspect-max-chunks"
            />
          </Field>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground mb-3">Conversation auto-title</div>
        <p className="text-xs text-muted-foreground mb-3">
          After the first user message (or auto-inspection), Faro calls the active model to generate a short conversation title shown in the sidebar. The prompt template is editable in <strong>Admin → Prompts → Title generator</strong>.
        </p>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={v("auto_title_enabled") ?? true}
            onChange={(e) => set("auto_title_enabled", e.target.checked)}
            className="h-4 w-4"
            data-testid="setting-auto-title-enabled"
          />
          <span className="font-semibold">Auto-generate conversation titles</span>
        </label>
      </div>

      <div className="border-t border-border pt-4">
        <Button onClick={save} disabled={!dirty || saving} className="rounded-sm" data-testid="settings-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity pricing
// ---------------------------------------------------------------------------
const MODES = [
  { value: "passthrough", label: "Cost passthrough (auto markup)" },
  { value: "flat", label: "Flat tokens per event" },
  { value: "per_unit", label: "Per-unit rate" },
];
const RATE_UNITS = [
  { value: "", label: "—" },
  { value: "per_minute", label: "per minute" },
  { value: "per_mb", label: "per MB" },
  { value: "per_chunk", label: "per chunk" },
  { value: "per_1k_chars", label: "per 1k chars" },
  { value: "per_call", label: "per call" },
];

function ActivitiesPanel() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/billing/activities");
    setRows(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const update = async (slug, patch) => {
    setBusy(slug);
    try {
      await api.patch(`/admin/billing/activities/${slug}`, patch);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="border border-border rounded-sm overflow-hidden" data-testid="activities-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/30 text-[11px] uppercase tracking-wider font-mono">
          <tr>
            <th className="text-left p-3 font-semibold">Activity</th>
            <th className="text-left p-3 font-semibold">Pricing mode</th>
            <th className="text-left p-3 font-semibold">Rate</th>
            <th className="text-left p-3 font-semibold">Flat</th>
            <th className="text-center p-3 font-semibold">Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.slug} className="border-t border-border" data-testid={`activity-row-${r.slug}`}>
              <td className="p-3">
                <div className="font-semibold">{r.label}</div>
                <div className="text-[10px] font-mono text-muted-foreground">{r.slug}</div>
              </td>
              <td className="p-3">
                <select
                  value={r.mode}
                  onChange={(e) => update(r.slug, { mode: e.target.value })}
                  className="h-8 px-2 rounded-sm border border-border bg-background text-xs font-mono"
                  disabled={busy === r.slug}
                  data-testid={`mode-${r.slug}`}
                >
                  {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </td>
              <td className="p-3">
                {r.mode === "per_unit" ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number" step="0.1" min="0" value={r.rate_tokens || 0}
                      onChange={(e) => update(r.slug, { rate_tokens: parseFloat(e.target.value) })}
                      className="h-8 w-20 rounded-sm font-mono"
                      data-testid={`rate-tokens-${r.slug}`}
                    />
                    <select
                      value={r.rate_unit || ""}
                      onChange={(e) => update(r.slug, { rate_unit: e.target.value })}
                      className="h-8 px-2 rounded-sm border border-border bg-background text-xs font-mono"
                      data-testid={`rate-unit-${r.slug}`}
                    >
                      {RATE_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  </div>
                ) : (
                  <span className="text-xs font-mono text-muted-foreground">{r.mode === "passthrough" ? "auto" : "—"}</span>
                )}
              </td>
              <td className="p-3">
                {(r.mode === "flat" || r.mode === "per_unit") ? (
                  <Input
                    type="number" step="1" min="0" value={r.flat_tokens || 0}
                    onChange={(e) => update(r.slug, { flat_tokens: parseInt(e.target.value, 10) || 0 })}
                    className="h-8 w-20 rounded-sm font-mono"
                    data-testid={`flat-${r.slug}`}
                  />
                ) : (
                  <span className="text-xs font-mono text-muted-foreground">—</span>
                )}
              </td>
              <td className="p-3 text-center">
                <input
                  type="checkbox" checked={!!r.active}
                  onChange={(e) => update(r.slug, { active: e.target.checked })}
                  className="h-4 w-4"
                  data-testid={`active-${r.slug}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Packages CRUD
// ---------------------------------------------------------------------------
function PackagesPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | row
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/billing/packages");
    setRows(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ name: "", tokens: 1000, price_usd: 15, badge: "", sort: 100, active: true });

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const body = {
        name: editing.name,
        tokens: parseInt(editing.tokens, 10),
        price_usd: parseFloat(editing.price_usd),
        badge: (editing.badge || "").trim() || null,
        sort: parseInt(editing.sort, 10) || 100,
        active: !!editing.active,
      };
      if (!body.name?.trim()) throw new Error("Name required");
      if (!body.tokens || body.tokens < 1) throw new Error("Tokens must be ≥ 1");
      if (!body.price_usd || body.price_usd <= 0) throw new Error("Price must be > 0");
      if (editing.id) {
        await api.patch(`/admin/billing/packages/${editing.id}`, body);
      } else {
        await api.post("/admin/billing/packages", body);
      }
      setEditing(null);
      await load();
      toast.success("Package saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this package?")) return;
    try {
      await api.delete(`/admin/billing/packages/${id}`);
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="packages-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono text-muted-foreground">Token bundles users can buy via Stripe.</div>
        <Button size="sm" onClick={startNew} className="rounded-sm" data-testid="package-new">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> New package
        </Button>
      </div>

      {editing && (
        <div className="border border-primary/40 bg-primary/5 rounded-sm p-4" data-testid="package-edit-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-sm">{editing.id ? "Edit package" : "New package"}</div>
            <button onClick={() => setEditing(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Name"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-sm" data-testid="pkg-name" /></Field>
            <Field label="Tokens"><Input type="number" min="1" value={editing.tokens} onChange={(e) => setEditing({ ...editing, tokens: e.target.value })} className="rounded-sm font-mono" data-testid="pkg-tokens" /></Field>
            <Field label="Price USD"><Input type="number" step="0.01" min="0.01" value={editing.price_usd} onChange={(e) => setEditing({ ...editing, price_usd: e.target.value })} className="rounded-sm font-mono" data-testid="pkg-price" /></Field>
            <Field label="Badge (optional)"><Input value={editing.badge || ""} placeholder='e.g. "Most popular"' onChange={(e) => setEditing({ ...editing, badge: e.target.value })} className="rounded-sm" data-testid="pkg-badge" /></Field>
            <Field label="Sort order"><Input type="number" value={editing.sort} onChange={(e) => setEditing({ ...editing, sort: e.target.value })} className="rounded-sm font-mono" data-testid="pkg-sort" /></Field>
            <Field label=" ">
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4" data-testid="pkg-active" />
                <span>Active (visible to users)</span>
              </label>
            </Field>
          </div>
          <div className="mt-4 flex gap-2">
            <Button size="sm" onClick={save} disabled={busy} className="rounded-sm" data-testid="pkg-save">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-[11px] uppercase tracking-wider font-mono">
            <tr>
              <th className="text-left p-3 font-semibold">Name</th>
              <th className="text-right p-3 font-semibold">Tokens</th>
              <th className="text-right p-3 font-semibold">Price</th>
              <th className="text-right p-3 font-semibold">$/1k</th>
              <th className="text-left p-3 font-semibold">Badge</th>
              <th className="text-center p-3 font-semibold">Active</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`package-row-${r.id}`}>
                <td className="p-3 font-semibold">{r.name}</td>
                <td className="p-3 text-right font-mono tabular-nums">{Number(r.tokens).toLocaleString()}</td>
                <td className="p-3 text-right font-mono tabular-nums">${Number(r.price_usd).toFixed(2)}</td>
                <td className="p-3 text-right font-mono tabular-nums text-muted-foreground">${(r.price_usd / r.tokens * 1000).toFixed(2)}</td>
                <td className="p-3"><span className="text-xs">{r.badge || ""}</span></td>
                <td className="p-3 text-center">{r.active ? <span className="text-emerald-600 dark:text-emerald-400 text-xs font-mono">●</span> : <span className="text-muted-foreground text-xs font-mono">○</span>}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing(r)} className="text-muted-foreground hover:text-foreground p-1" data-testid={`pkg-edit-${r.id}`}><Edit2 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive p-1 ml-1" data-testid={`pkg-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (<tr><td colSpan={7} className="p-6 text-center text-sm text-muted-foreground font-mono">No packages yet.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// User wallets + manual adjust
// ---------------------------------------------------------------------------
function UsersPanel() {
  const [rows, setRows] = useState([]);
  const [adjusting, setAdjusting] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/billing/users");
    setRows(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!filter) return rows;
    const f = filter.toLowerCase();
    return rows.filter((r) => (r.email || "").toLowerCase().includes(f) || (r.name || "").toLowerCase().includes(f));
  }, [rows, filter]);

  return (
    <div className="space-y-3" data-testid="users-wallets-panel">
      <div className="flex items-center justify-between">
        <Input placeholder="Filter by email / name" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-sm w-72 h-9" data-testid="user-filter" />
        <Button size="sm" variant="ghost" onClick={load} className="rounded-sm">Refresh</Button>
      </div>

      {adjusting && <AdjustModal user={adjusting} onClose={() => { setAdjusting(null); load(); }} />}

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-[11px] uppercase tracking-wider font-mono">
            <tr>
              <th className="text-left p-3 font-semibold">User</th>
              <th className="text-right p-3 font-semibold">Balance</th>
              <th className="text-right p-3 font-semibold">Purchased</th>
              <th className="text-right p-3 font-semibold">Bonus</th>
              <th className="text-right p-3 font-semibold">Spent</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`user-wallet-${r.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{r.name || "(no name)"}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{r.email}</div>
                </td>
                <td className="p-3 text-right font-mono tabular-nums font-bold">{Number(r.balance_tokens).toLocaleString()}</td>
                <td className="p-3 text-right font-mono tabular-nums text-muted-foreground">{Number(r.lifetime_purchased).toLocaleString()}</td>
                <td className="p-3 text-right font-mono tabular-nums text-muted-foreground">{Number(r.lifetime_bonus).toLocaleString()}</td>
                <td className="p-3 text-right font-mono tabular-nums text-muted-foreground">{Number(r.lifetime_spent).toLocaleString()}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setAdjusting(r)} className="h-7 rounded-sm text-xs" data-testid={`adjust-${r.id}`}>Adjust</Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (<tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground font-mono">No users.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdjustModal({ user, onClose }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const d = parseInt(delta, 10);
    if (!d || d === 0) return toast.error("Enter a non-zero token amount");
    if (!reason.trim() || reason.trim().length < 2) return toast.error("Reason required");
    setBusy(true);
    try {
      await api.post(`/admin/billing/users/${user.id}/adjust`, { delta: d, reason: reason.trim() });
      toast.success(`Adjusted ${d > 0 ? "+" : ""}${d} tokens for ${user.email}`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Adjust failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm grid place-items-center z-50 p-4" data-testid="adjust-modal">
      <div className="w-full max-w-md bg-card border border-border rounded-sm p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold tracking-tight text-lg">Adjust wallet</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="text-xs font-mono text-muted-foreground mt-1">{user.email} · current: <b>{user.balance_tokens.toLocaleString()}</b> tokens</div>
        <div className="mt-4 space-y-3">
          <Field label="Delta (positive = credit, negative = debit)">
            <Input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="e.g. 500 or -100" className="rounded-sm font-mono" data-testid="adjust-delta" />
          </Field>
          <Field label="Reason (audit)">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you adjusting?" className="rounded-sm" data-testid="adjust-reason" />
          </Field>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-sm" data-testid="adjust-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Apply
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stripe transactions log
// ---------------------------------------------------------------------------
function TransactionsPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/billing/transactions").then(({ data }) => setRows(data.items || []));
  }, []);

  return (
    <div className="border border-border rounded-sm overflow-hidden" data-testid="transactions-panel">
      <table className="w-full text-sm">
        <thead className="bg-muted/30 text-[11px] uppercase tracking-wider font-mono">
          <tr>
            <th className="text-left p-3 font-semibold">When</th>
            <th className="text-left p-3 font-semibold">User</th>
            <th className="text-left p-3 font-semibold">Package</th>
            <th className="text-right p-3 font-semibold">Amount</th>
            <th className="text-right p-3 font-semibold">Tokens</th>
            <th className="text-left p-3 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border">
              <td className="p-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
              <td className="p-3 font-mono text-xs">{r.user_email}</td>
              <td className="p-3">{r.package_name}</td>
              <td className="p-3 text-right font-mono tabular-nums">${Number(r.amount_usd).toFixed(2)}</td>
              <td className="p-3 text-right font-mono tabular-nums">{Number(r.tokens).toLocaleString()}</td>
              <td className="p-3"><TxStatusBadge status={r.status} credited={r.credited} /></td>
            </tr>
          ))}
          {rows.length === 0 && (<tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground font-mono">No transactions yet.</td></tr>)}
        </tbody>
      </table>
    </div>
  );
}

function TxStatusBadge({ status, credited }) {
  if (status === "paid" && credited) return <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Paid · credited</span>;
  if (status === "pending") return <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-amber-500/15 text-amber-700 dark:text-amber-300">Pending</span>;
  if (status === "expired") return <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-muted text-muted-foreground">Expired</span>;
  return <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-muted text-muted-foreground">{status || "?"}</span>;
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-mono">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[11px] text-muted-foreground mt-1 font-mono">{hint}</p>}
    </div>
  );
}
